import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FormControl } from '@material-ui/core';
import { useTypedTranslation } from '@app/hooks';
import { loadingData, getModalSwitch } from '@app/store/actions';
import { infiniteScrollItemsFetchLimit, useDebounce, onPressEnterModal, emptyGuid } from '@app/helpers';
import { IActiveFilter, FilterEnum } from '@app/models';
import { ModalActionsComponent, PasslySelect, AddEditApp, PageLoader, PasslyTextField } from '@app/components';
import { appLibraryService } from '@app/services';
import { AxiosResponse } from 'axios';
import { AppStageType, AppViewType, PROTOCOL_TYPES_VALUE, DEEP_LINKS } from '../addEditApplication.enums';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ApplicationInterfaceTypeUnion } from '@app/components/SSOApps/addEditAppTabs/appContext';


interface IProps {
    specificAppName: string;
}
const AddApplicationListDialog: React.SFC<IProps> = ({ specificAppName }) => {
    // -------------------------------------------
    // ------------------ DATA -------------------
    // -------------------------------------------
    const isAddingSpecificApp = specificAppName !== '';

    // Federated sp-init, idp-init, ws-fed
    const [appCatalogListTotalCount, setAppCatalogListTotalCount] = useState(44);

    // web workflow
    const [webCatalogListTotalCount, setWebCatalogListTotalCount] = useState(0);
    const [webSkipCount, setWebSkipCount] = useState(0);

    // the rest
    const [rows, setRows] = useState<any[]>([]);
    const [skipCount, setSkipCount] = useState(0);
    const [webCatalogCategories, setWebCatalogCategories] = useState<any[]>([]);
    const [selectedWebCatalogFilter, setSelectedWebCatalogFilter] = useState(0);
    const [searchField, setSearchField] = useState('');
    const [initialLoad, setInitialLoad] = useState(true);
    const [viewType, setViewType] = useState(AppViewType.addAppListView);
    const [selectedApp, setSelectedApp] = useState({});

    const dispatch = useDispatch();
    const debouncedSearch = useDebounce(searchField, 500);

    const { translation } = useTypedTranslation();

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useEffect(() => {
        if (isAddingSpecificApp) {
            appLibraryService.getSpecificCatalogApp(specificAppName, handleGetSpecificAppSuccess);
        } else {
            dispatch(loadingData(false));
            appLibraryService.getCatalogCategories().then((response: AxiosResponse) => {
                setWebCatalogCategories([{ Label: 'CATALOG_CATEGORY_0', CategoryId: 0 }].concat(response.data.value));
            });
        }
    }, []);

    useEffect(() => {
        if ((debouncedSearch === '' || selectedWebCatalogFilter === 0) && initialLoad) {
            setInitialLoad(false);
        } else {
            setAppCatalogListTotalCount(0);
            setWebCatalogListTotalCount(0);
            setInitialLoad(true);
            setSkipCount(0);
            setRows([]);
        }
    }, [debouncedSearch, selectedWebCatalogFilter]);

    useEffect(() => {
        if (initialLoad && !isAddingSpecificApp) {
            fetchMoreData(true);
            setInitialLoad(false);
        }
    }, [initialLoad]);

    // -------------------------------------------
    // --------------- FUNCTIONS -----------------
    // -------------------------------------------

    const getFilter = (): IActiveFilter[] => {
        let activeFilters: IActiveFilter[] = [];
        if (searchField !== '') {
            activeFilters.push({
                filterBy: 'DisplayName',
                value: searchField,
                label: 'DisplayName',
                name: FilterEnum.contains
            });
        }

        if (selectedWebCatalogFilter !== 0) {
            activeFilters.push({
                value: selectedWebCatalogFilter.toString(),
                label: FilterEnum.categoryId,
                name: FilterEnum.categoryId
            });
        }

        return activeFilters;
    };

    const getWebCatalog = (initialLoad?: boolean, continuedCatalogRows?: any[]) => {
        let webSkipCountToUse: number = rows.length === appCatalogListTotalCount || initialLoad ? 0 : webSkipCount;
        let isWebReset = webSkipCountToUse === 0 || initialLoad;

        appLibraryService.getWebCatalog(infiniteScrollItemsFetchLimit, webSkipCountToUse, isWebReset, getFilter()).then((response: AxiosResponse) => {
            if (isWebReset) {
                setWebCatalogListTotalCount(response.data['@odata.count'] as number);
            }
            let newRows: any[] = response.data.value as any[];

            continuedCatalogRows !== undefined ? setRows(rows.concat(continuedCatalogRows.concat(newRows))) : setRows(rows.concat(newRows));
        });

        setWebSkipCount(webSkipCountToUse + infiniteScrollItemsFetchLimit);
    };

    const fetchMoreData = (isInitialLoad?: boolean) => {
        let nextScrollCount = skipCount + infiniteScrollItemsFetchLimit;
        let countToUse = isInitialLoad ? 0 : skipCount;

        isInitialLoad ? setSkipCount(infiniteScrollItemsFetchLimit) : setSkipCount(nextScrollCount);

        if (selectedWebCatalogFilter !== 0) {
            getWebCatalog(isInitialLoad);
        } else if (rows.length < appCatalogListTotalCount || isInitialLoad) {
            appLibraryService.getCatalog(infiniteScrollItemsFetchLimit, countToUse, isInitialLoad, getFilter()).then((response: AxiosResponse) => {
                let newRows: any[] = [];

                if (initialLoad) {
                    setAppCatalogListTotalCount(response.data['@odata.count'] as number);

                    // setup default custom catalog app
                    newRows.push({
                        '@odata.type': '#Kaseya.AuthAnvil.Models.CatalogApplication',
                        Id: 'custom',
                        DisplayName: 'CUSTOM_APP_NAME',
                        AccessTokenLifetime: 'PT60M',
                        Enabled: false,
                        AttributeRules: `{"Entries": []}`,
                        IconUrl: window.location.origin + '/assets/images/apps/app-large.png',
                        Description: 'CUSTOM_APP_DESCRIPTION',
                        ServiceConfiguration: '{}',
                        Company: '',
                        AudienceUris: ['https://example.com'],
                        ReplyToUrl: 'https://example.com/sample/endpoint',
                        ServiceIssuerUri: 'https://example.com',
                        IdentityIssuerUri: 'https://hardcoded/value',
                        Protocol: PROTOCOL_TYPES_VALUE.spInit,
                        Protocols: ['APP_PROTOCOL_SP_INIT', 'APP_PROTOCOL_IDP_INIT']
                    });
                }
                if (response.data.value && response.data.value.length) {
                    response.data.value.forEach(function (app: any) {
                        app.Protocols = JSON.parse(app.Protocols);
                        newRows.push(app);
                    });
                }

                if (rows.length + response.data.value.length >= appCatalogListTotalCount) {
                    // we've reached the end, now get initial web catalog
                    getWebCatalog(true, newRows);
                } else {
                    setRows(rows.concat(newRows));
                }
            });
        } else {
            getWebCatalog(false);
        }
    };

    const handleAddApplicationConfigClick = (app: any) => {
        app.Description = null;
        app.PolicyId = '';

        if (app.Id === 'custom') {
            app.Id = emptyGuid;
            app.DisplayName = '';
            setSelectedApp(app);
            setViewType(AppViewType.addCustomApp);
        } else if (app['@odata.type'] === '#Kaseya.AuthAnvil.Models.CatalogApplication') {
            app.Id = emptyGuid;
            if (app.DisplayName.toLowerCase() === 'office 365') {
                let serviceConfig = JSON.parse(app.ServiceConfiguration);
                serviceConfig['deep-links'] = JSON.stringify(DEEP_LINKS);
                app.ServiceConfiguration = JSON.stringify(serviceConfig);
            }
            setSelectedApp(app);
            setViewType(AppViewType.addCatalogApp);
        } else {
            app.Id = emptyGuid;
            setSelectedApp(app);
            setViewType(AppViewType.addWebWorkflowApp);
        }
    };

    const handleGetSpecificAppSuccess = (response: any) => {
        let appToLoad = response.data.value[0];
        appToLoad.Protocols = JSON.parse(appToLoad.Protocols);
        handleAddApplicationConfigClick(appToLoad);
    };

    const handleWebCatalogFilter = (e: any) => {
        setSelectedWebCatalogFilter(e.target.value);
    };

    const getDescription = (row: any) => {
        if (row['@odata.type'] === '#Kaseya.AuthAnvil.Models.CatalogApplication') {
            return translation(row.Description);
        } else {
            if (row.CategoryName) {
                let parts = row.CategoryName.split('\\');
                if (parts.length > 1) {
                    return parts[1];
                } else {
                    return parts[0];
                }
            } else {
                return '';
            }
        }
    };

    const getProtocols = (row: any) => {
        if (row['@odata.type'] === '#Kaseya.AuthAnvil.Models.CatalogApplication') {
            if (!row.Protocols) {
                return '';
            }

            let translatedProtocols: string[] = [];
            row.Protocols.forEach(function (protocol: any) {
                translatedProtocols.push(translation(protocol));
            });

            return translatedProtocols.join(', ');
        } else {
            return 'Web Workflow';
        }
    };

    const getOrganization = (row: any) => {
        if (row['@odata.type'] === '#Kaseya.AuthAnvil.Models.CatalogApplication') {
            try {
                let config = JSON.parse(row.ServiceConfiguration);

                if (config && config.Organization) {
                    return config.Organization;
                }

                return null;
            } catch (e) {
                return null;
            }
        } else {
            return row.Company;
        }
    };

    const closeConfirmModal = () => {
        dispatch(getModalSwitch(false));
    };

    return (
        <>
                {viewType === AppViewType.addAppListView ? (
                    <>
                        {!isAddingSpecificApp ? (
                            <div className='modal__identities-list'  tabIndex={0} onKeyPress={onPressEnterModal(closeConfirmModal)}>
                                <div className='modal__identities-list--rowfilter'>
                                    <FormControl className='modal__single--filter modal__single--filter--add-app'>
                                        <PasslyTextField
                                            label={translation('SEARCH_FOR_APP')}
                                            type='text'
                                            value={searchField}
                                            onChange={(e) => setSearchField(e.target.value)}
                                            autoFocus
                                        />
                                    </FormControl>
                                    <FormControl className='modal__single--filter'>
                                        <PasslySelect
                                            value={selectedWebCatalogFilter}
                                            onChange={(event) => handleWebCatalogFilter(event)}
                                            label={'WORKFLOW_CATEGORY'}
                                            options={webCatalogCategories}
                                            optionLabel='Label'
                                            optionValue='CategoryId'
                                        />
                                    </FormControl>
                                </div>
                                <div className='modal__identities-list--data'>
                                    <div id='scrollableAreaApps' className='scrollableTable'>
                                        {/* Can't use passly infinite scroll since we're populating from two separate APIs (special case) */}
                                        <InfiniteScroll
                                            dataLength={rows.length}
                                            hasMore={rows.length < appCatalogListTotalCount + webCatalogListTotalCount + 1}
                                            next={() => fetchMoreData()}
                                            loader={<></>}
                                            scrollableTarget='scrollableAreaApps'
                                            scrollThreshold='100%'
                                        >
                                            {rows.length !== 0 &&
                                                rows.map((row) => (
                                                    <div
                                                        className='modal__identities-list--app'
                                                        key={row.Id}
                                                        onClick={() => handleAddApplicationConfigClick(row)}
                                                    >
                                                        <div className='modal__identities-list--icon'>
                                                            <img src={row.IconUrl} />
                                                        </div>
                                                        <div className='modal__identities-list--app__col'>
                                                            <div className='modal__identities-list--appname'>{translation(row.DisplayName)}</div>
                                                            <div>{getOrganization(row)}</div>
                                                        </div>
                                                        <div className='modal__identities-list--app__col'>{getDescription(row)}</div>

                                                        <div className='modal__identities-list--app__col'>{getProtocols(row)}</div>
                                                    </div>
                                                ))}
                                        </InfiniteScroll>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <PageLoader />
                        )}
                    </>
                ) : (
                    <AddEditApp
                        appStageType={AppStageType.addApp}
                        closeListModal={closeConfirmModal}
                        app={selectedApp as ApplicationInterfaceTypeUnion}
                    />
                )}
                {/* Handle disabled when implementing add application portion */}
            {viewType === AppViewType.addAppListView && (
                <ModalActionsComponent secondaryBtn={translation('CANCEL')} onClickSecondary={closeConfirmModal} disabled={true} id='addAppList' />
            )}
        </>
    );
};

export default AddApplicationListDialog;
